import i18nConfig from "@/modules/language/configuration/i18nConfig";
import fedidService from "@/modules/oauth/services/FedidService";
import useAuth from "@/modules/oauth/composables/useAuth";

const { t } = i18nConfig.global;

interface ApiErrorBody {
  message?: string;
  detail?: string;
  details?: unknown;
  code?: string;
}

interface ApiError {
  code?: string;
  response?: {
    status: number;
    data?: ApiErrorBody;
  };
}

function resolveApiErrorText(data?: ApiErrorBody): string | undefined {
  if (!data) return undefined;

  if (typeof data.message === "string" && data.message.trim().length > 0) {
    return data.message;
  }

  if (typeof data.detail === "string" && data.detail.trim().length > 0) {
    return data.detail;
  }

  if (Array.isArray(data.details)) {
    const firstDetail = data.details.find((entry) => typeof entry === "string");
    if (typeof firstDetail === "string" && firstDetail.trim().length > 0) {
      return firstDetail;
    }
  }

  return undefined;
}

const checkErrorsApiResponse = (error: ApiError): { icon: "error" | "success"; title: string; text?: string } => {
  if (error.code === "ERR_NETWORK") {
    return {
      icon: "error",
      title: t("errNetwork"),
    };
  }

  if (error.response?.data?.code === "VISITORS_DUPLICATE_BADGE") {
    return {
      icon: "error",
      title: t("err409"),
      text: t("visitors_duplicate_badge"),
    };
  }

  if (error.response?.data?.code === "VISITORS_DATA_INTEGRITY_CONFLICT") {
    return {
      icon: "error",
      title: t("err409"),
      text: t("visitors_data_integrity_conflict"),
    };
  }

  if (error.response?.data?.code === "VISITORS_INTERNAL_ERROR") {
    return {
      icon: "error",
      title: t("err500"),
      text: t("visitors_internal_error"),
    };
  }

  if (error.response?.status === 401) {
    fedidService
      .refreshTokenAndSaveData(false)
      .then(() => {
        //NO ACTION
      })
      .catch(() => {
        useAuth().revokeSession();
        globalThis.location.href = globalThis.location.origin;
      });
    return {
      icon: "success",
      title: "ok",
    };
  }

  if (error.response?.status === 403) {
    return {
      icon: "error",
      title: t("err403"),
    };
  }

  if (error.response?.status === 404) {
    return {
      icon: "error",
      title: t("err404"),
      text: resolveApiErrorText(error.response.data),
    };
  }

  if (error.response?.status === 400) {
    return {
      icon: "error",
      title: t("err400"),
      text: resolveApiErrorText(error.response.data),
    };
  }

  if (error.response?.status === 409) {
    console.log("checkErrorsApiResponse 409 error", error.response.data?.code);
    return {
      icon: "error",
      title: t("err409"),
      text: resolveApiErrorText(error.response.data),
    };
  }

  if (error.response?.status === 422) {
    return {
      icon: "error",
      title: t("err400"),
      text: resolveApiErrorText(error.response.data),
    };
  }

  if (error.response?.status === 500) {
    return {
      icon: "error",
      title: t("err500"),
      text: t("error_500"),
    };
  }

  if (error.response?.status && error.response.status > 500) {
    return {
      icon: "error",
      title: t("err501"),
    };
  }

  return {
    icon: "success",
    title: t("ok"),
  };
};

export default checkErrorsApiResponse;
